import { useEffect, useMemo, useState } from 'react';
import { Calculator, Check, DollarSign, Gauge, Leaf, X } from 'lucide-react';
import FieldHelp from './FieldHelp';

const MATERIALS = ['PLA', 'PETG', 'ABS', 'ASA', 'TPU', 'Nylon', 'PC', 'Resin', 'Other'];

const emptyProfile = {
  name: '',
  materialType: '',
  brand: '',
  spoolPrice: '',
  spoolWeightGrams: '',
  electricityRate: '',
  machineRate: '',
  laborRate: '',
  wastePercent: '',
  fixedCost: '',
  targetMargin: '',
  isDefault: false,
};

function toFormValues(profile) {
  if (!profile) return emptyProfile;
  return {
    ...emptyProfile,
    ...profile,
    name: profile.name || '',
    brand: profile.brand || '',
  };
}

function numeric(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function FieldHeading({ htmlFor, label, optional, guidanceKey, note }) {
  return (
    <span className="field__heading">
      <label htmlFor={htmlFor}>{label}{optional && <small>Optional</small>}{note && <small>{note}</small>}</label>
      <FieldHelp guidanceKey={guidanceKey} />
    </span>
  );
}

export default function CostProfileForm({ profile, isFirstProfile, onClose, onSave, saving }) {
  const [values, setValues] = useState(() => toFormValues(profile));
  const [error, setError] = useState('');
  const isEditing = Boolean(profile?.id);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !saving) onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose, saving]);

  const costPerGram = useMemo(() => {
    const weight = numeric(values.spoolWeightGrams);
    return weight > 0 ? numeric(values.spoolPrice) / weight : 0;
  }, [values.spoolPrice, values.spoolWeightGrams]);

  const hasMaterialCost = values.spoolPrice !== '' && numeric(values.spoolWeightGrams) > 0;

  const update = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setValues((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!values.name.trim()) {
      setError('Give this cost profile a name.');
      return;
    }
    if (!values.materialType) {
      setError('Choose a material type.');
      return;
    }
    if (numeric(values.spoolWeightGrams) <= 0) {
      setError('Spool weight must be greater than zero.');
      return;
    }
    if (numeric(values.targetMargin) < 0 || numeric(values.targetMargin) >= 95) {
      setError('Target margin must be between 0% and 94.99%.');
      return;
    }

    const normalized = {
      name: values.name.trim(),
      materialType: values.materialType,
      brand: values.brand.trim(),
      spoolPrice: numeric(values.spoolPrice),
      spoolWeightGrams: numeric(values.spoolWeightGrams),
      electricityRate: numeric(values.electricityRate),
      machineRate: numeric(values.machineRate),
      laborRate: numeric(values.laborRate),
      wastePercent: numeric(values.wastePercent),
      fixedCost: numeric(values.fixedCost),
      targetMargin: numeric(values.targetMargin),
      isDefault: isFirstProfile || Boolean(values.isDefault),
    };

    try {
      await onSave(normalized);
    } catch (saveError) {
      setError(saveError?.message || 'The profile could not be saved.');
    }
  };

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && !saving && onClose()}>
      <section className="profile-modal" role="dialog" aria-modal="true" aria-labelledby="profile-modal-title">
        <header className="profile-modal__header">
          <div>
            <p className="eyebrow">Cost profile</p>
            <h2 id="profile-modal-title">{isEditing ? 'Edit pricing assumptions' : 'Create a cost profile'}</h2>
            <p>Save the real costs MakerMargin will use when pricing your prints. Select any help icon for calculation guidance.</p>
          </div>
          <button className="icon-button" type="button" onClick={onClose} disabled={saving} aria-label="Close cost profile">
            <X size={20} />
          </button>
        </header>

        <form className="profile-form" onSubmit={handleSubmit}>
          <fieldset>
            <legend><Leaf size={17} /> Profile identity</legend>
            <div className="profile-form__grid profile-form__grid--identity">
              <div className="field">
                <FieldHeading htmlFor="profile-name" label="Profile name" guidanceKey="profileName" />
                <span className="field__control"><input id="profile-name" autoFocus required value={values.name} onChange={update('name')} placeholder="Name this cost profile" /></span>
              </div>
              <div className="field">
                <FieldHeading htmlFor="profile-material" label="Material" guidanceKey="materialType" />
                <span className="field__control field__control--select">
                  <select id="profile-material" value={values.materialType} onChange={update('materialType')}>
                    <option value="">Select material</option>
                    {MATERIALS.map((material) => <option value={material} key={material}>{material}</option>)}
                  </select>
                </span>
              </div>
              <div className="field profile-form__brand">
                <FieldHeading htmlFor="profile-brand" label="Brand or product line" optional guidanceKey="brand" />
                <span className="field__control"><input id="profile-brand" value={values.brand} onChange={update('brand')} placeholder="Enter the brand or product line" /></span>
              </div>
            </div>
          </fieldset>

          <fieldset>
            <legend><DollarSign size={17} /> Material and operating costs</legend>
            <div className="profile-form__grid">
              <div className="field">
                <FieldHeading htmlFor="profile-spool-price" label="Spool price" guidanceKey="spoolPrice" />
                <span className="field__control unit-field"><b>$</b><input id="profile-spool-price" type="number" inputMode="decimal" min="0" step="0.01" value={values.spoolPrice} onChange={update('spoolPrice')} /></span>
              </div>
              <div className="field">
                <FieldHeading htmlFor="profile-spool-weight" label="Spool weight" guidanceKey="spoolWeightGrams" />
                <span className="field__control unit-field"><input id="profile-spool-weight" type="number" inputMode="decimal" min="1" step="1" value={values.spoolWeightGrams} onChange={update('spoolWeightGrams')} /><b>g</b></span>
              </div>
              <div className="field">
                <FieldHeading htmlFor="profile-electricity" label="Electricity rate" guidanceKey="electricityRate" />
                <span className="field__control unit-field"><b>$</b><input id="profile-electricity" type="number" inputMode="decimal" min="0" step="0.001" value={values.electricityRate} onChange={update('electricityRate')} /><b>/kWh</b></span>
              </div>
              <div className="field">
                <FieldHeading htmlFor="profile-machine" label="Machine cost" note="Wear and replacement reserve" guidanceKey="machineRate" />
                <span className="field__control unit-field"><b>$</b><input id="profile-machine" type="number" inputMode="decimal" min="0" step="0.01" value={values.machineRate} onChange={update('machineRate')} /><b>/hr</b></span>
              </div>
              <div className="field">
                <FieldHeading htmlFor="profile-labor" label="Labor rate" guidanceKey="laborRate" />
                <span className="field__control unit-field"><b>$</b><input id="profile-labor" type="number" inputMode="decimal" min="0" step="0.01" value={values.laborRate} onChange={update('laborRate')} /><b>/hr</b></span>
              </div>
              <div className="field">
                <FieldHeading htmlFor="profile-fixed" label="Fixed cost per job" guidanceKey="fixedCost" />
                <span className="field__control unit-field"><b>$</b><input id="profile-fixed" type="number" inputMode="decimal" min="0" step="0.01" value={values.fixedCost} onChange={update('fixedCost')} /></span>
              </div>
            </div>
          </fieldset>

          <fieldset>
            <legend><Gauge size={17} /> Risk and margin</legend>
            <div className="profile-form__grid profile-form__grid--risk">
              <div className="field">
                <FieldHeading htmlFor="profile-waste" label="Waste / failure allowance" guidanceKey="wastePercent" />
                <span className="field__control unit-field"><input id="profile-waste" type="number" inputMode="decimal" min="0" max="100" step="0.1" value={values.wastePercent} onChange={update('wastePercent')} /><b>%</b></span>
              </div>
              <div className="field">
                <FieldHeading htmlFor="profile-margin" label="Default target margin" guidanceKey="targetMargin" />
                <span className="field__control unit-field"><input id="profile-margin" type="number" inputMode="decimal" min="0" max="94.99" step="0.1" value={values.targetMargin} onChange={update('targetMargin')} /><b>%</b></span>
              </div>
              <label className={`default-toggle ${isFirstProfile ? 'default-toggle--locked' : ''}`}>
                <input type="checkbox" checked={isFirstProfile || values.isDefault} onChange={update('isDefault')} disabled={isFirstProfile} />
                <span className="default-toggle__box"><Check size={15} /></span>
                <span><strong>Use as my default</strong><small>{isFirstProfile ? 'Your first profile becomes the default automatically.' : 'New estimates will start with this profile.'}</small></span>
              </label>
            </div>
          </fieldset>

          <aside className="profile-form__preview" aria-label="Live profile preview">
            <span className="profile-form__preview-icon"><Calculator size={22} /></span>
            <div><small>Calculated material cost</small><strong>{hasMaterialCost ? `$${costPerGram.toFixed(4)} per gram` : 'Waiting for your values'}</strong></div>
            <div><small>Profile status</small><strong>{hasMaterialCost ? 'Using your saved costs' : 'No assumptions preloaded'}</strong><span>Estimates will use only the values you enter.</span></div>
            <Check size={18} />
          </aside>

          {error && <p className="form-status form-status--error" role="alert">{error}</p>}

          <footer className="profile-modal__actions">
            <button className="button button--ghost" type="button" onClick={onClose} disabled={saving}>Cancel</button>
            <button className="button button--primary" type="submit" disabled={saving}>{saving ? 'Saving…' : isEditing ? 'Save changes' : 'Create profile'}</button>
          </footer>
        </form>
      </section>
    </div>
  );
}
